import { axiosInstance } from '@/http-common'

/**
 * Set a Modbus parameter value on a device
 * @param serialNumber - Asset serial number
 * @param paramName - Parameter name
 * @param value - Value to set
 */
export async function setModbusAssetParam(serialNumber: string, paramName: string, value: string): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.post('/api/v1/modbus/set-asset-param', {
      serial_number: serialNumber,
      param_name: paramName,
      value
    })
      .then(response => resolve(response))
      .catch(error => reject(error))
  })
}

/**
 * Send a DKN control panel action
 * @param serialNumber - Asset serial number
 * @param canonicalKey - Canonical action key (e.g. 'dkn.power.command')
 * @param value - Value to set (boolean for toggle/command, number for setpoint)
 */
export async function postDknControlAction(serialNumber: string, canonicalKey: string, value: unknown): Promise<any> {
  return axiosInstance.post(`/api/v1/dkn/control-panel/${encodeURIComponent(serialNumber)}/action`, {
    canonical_key: canonicalKey,
    value
  })
}

/**
 * Decode a raw Modbus read response into a human-readable value
 * @param paramName - Parameter name
 * @param rawResponse - Raw hex response string from the device
 */
export async function decodeModbusReadResponse(paramName: string, rawResponse: string): Promise<any> {
  return new Promise((resolve, reject) => {
    axiosInstance.post('/api/v1/modbus/decode-read-response', {
      param_name: paramName,
      raw_response: rawResponse
    })
      .then(response => resolve(response))
      .catch(error => reject(error))
  })
}

/**
 * Clear the stored serial command result for a device
 * @param imei - Device IMEI
 */
export async function clearDeviceSerialCommandResult(imei: string): Promise<void> {
  await axiosInstance.delete(`/api/v1/modbus/serial-result/${encodeURIComponent(imei)}`)
}

/**
 * Send a serial command to a device via Redis
 * @param imei - Device IMEI
 * @param commandText - Command text to send
 * @param ttlSeconds - TTL for the Redis key (default 60s)
 */
export async function setDeviceSerialCommand(imei: string, commandText: string, ttlSeconds = 60): Promise<void> {
  const sanitized = String(commandText || '').replace(/\r?\n/g, ' ').trim()
  if (!sanitized) throw new Error('Command cannot be empty.')
  await axiosInstance.post('/api/v1/modbus/serial-command', {
    imei,
    frame_hex: sanitized,
    ttl: ttlSeconds,
  })
}
